import Toolbox from '@/utils/toolbox'
import ImageLayer from 'ol/layer/Image'
import ImageWMS from 'ol/source/ImageWMS'
import Point from 'ol/geom/Point'
import WMSCapabilities from 'ol/format/WMSCapabilities'
import camelize from 'camelize'
import GeoJSON from '@/utils/geoJson'

import { get as getProj } from 'ol/proj'
import EPSG from '@/utils/epsg'

import Axios from 'axios'
import GeomToolbox from '@/utils/geomToolbox'

import Config from '@/config'
import VectorSource from 'ol/source/Vector'

export default class WmsLayer extends ImageLayer {
  static get serviceType () {
    return 'WMS'
  }

  constructor (source) {
    const layerProjection = new EPSG(getProj(source.projection))

    super({
      source: new ImageWMS({
        url: source.url,
        params: {
          'LAYERS': source.geoserverConfig.sourceName,
          ...source.geoserverConfig.params
        },
        matrixSet: source.projection,
        format: source.format,
        projection: layerProjection.projection,
        transition: 100
      }),
      opacity: Toolbox.getOrDefault(source.webviewConfig, 'opacity', 1)
    })

    // Valeurs par défaut

    this.sourceId = source.id
    this.metadata = source.metadata
    this.geomType = source.geomType

    this.geoserverConfig = {
      url: source.url,
      ...source.geoserverConfig
    }

    this.webviewConfig = source.webviewConfig
    this.webviewConfig.selected = false
    this.webviewConfig.showOnMap = Toolbox.getOrDefault(source.webviewConfig, 'showOnMap', true)
    this.webviewConfig.opacity = Toolbox.getOrDefault(source.webviewConfig, 'opacity', 1)

    return this
  }

  getCapabilities () {
    return new Promise((resolve, reject) => {
      Axios.get(`${this.getSource().getUrl()}&service=wms&request=GetCapabilities`).then((response) => {
        const parser = new WMSCapabilities()
        resolve(parser.read(response.data))
      }).catch(reject)
    })
  }

  getLayerCapabilities () {
    return new Promise((resolve, reject) => {
      this.getCapabilities().then((capabilities) => {
        resolve(capabilities.Capability.Layer.Layer.find((layer) => layer.Name === this.geoserverConfig.sourceName))
      }).catch(reject)
    })
  }

  getLayerExtent() {
    return new Promise((resolve, reject) => {
      this.getLayerCapabilities().then((capabilities) => {
        resolve(capabilities?.BoundingBox?.[0].extent)
      }).catch(reject)
    })
  }

  getProjectionCode () {
    return this.getSource().getProjection().getCode()
  }

  getFeaturesFromGeom (mapRef, geom, params = {}, projection = Config.mapConfiguration.projection) {
    return new Promise((resolve, reject) => {
      this.getFromGeom(mapRef, geom, params, projection).then((data) => {
        if (typeof data.features === 'undefined') return reject(data)
        resolve(camelize(data.features))
      })
    })
  }

  getFromGeom (mapRef, geom, params = {}, projection = Config.mapConfiguration.projection) {
    return new Promise((resolve, reject) => {
      params.INFO_FORMAT = Toolbox.getOrDefault(params, 'INFO_FORMAT', 'application/json')

      if (typeof this.geoserverConfig.getFeatureParams !== 'undefined') this.geoserverConfig.getFeatureParams.forEach((param) => {
        params[param.key] = param.value
      })

      // projection correspond à celle utilisé dans la carte
      const geomToRequest = GeomToolbox.transform(geom, projection, this.getProjectionCode())

      const url = this.getSource().getFeatureInfoUrl(
        geomToRequest.coordinates,
        mapRef.getView().getResolution(),
        this.getProjectionCode(),
        params
      )

      Axios.get(url).then((resp) => {
        resolve(camelize(resp.data))
      }).catch(reject)
    })
  }

  getFeaturesFromCoordinates (mapRef, coordinates, params = {}, projection = Config.mapConfiguration.projection) {
    let geom = JSON.parse(GeoJSON.writeGeometry(new Point(coordinates)))
    return this.getFeaturesFromGeom(mapRef, geom, params, projection)
  }

  searchFeatures (terms, propertyName = 'name') {
    return this.fetchFeatures({
      additionalParameters: `&Filter=<Filter><PropertyIsLike matchCase="false" wildcard='*' singleChar='.' escape='!'><PropertyName>${propertyName}</PropertyName><Literal>${terms}</Literal></PropertyIsLike></Filter>`,
      convertToFeatures: false
    })
  }

  fetchFeatures ({
    additionalParameters = '',
    convertToFeatures = true
  } = {}) {
    return new Promise((resolve, reject) => {
      let params = `&SERVICE=WFS&VERSION=2.0.0&REQUEST=GetFeature&TYPENAME=${this.geoserverConfig.sourceName}&OUTPUTFORMAT=application/json`
      let currentParams = this.getSource().getParams()
      Object.keys(currentParams).forEach((key) => {
        params += `&${key}=${currentParams[key]}`
      })
      params += additionalParameters
      let url = `${this.geoserverConfig.url}${params}`

      Axios.get(url)
      .then((resp) => {
        let toReturn = resp.data
        if (convertToFeatures) toReturn = GeoJSON.readFeatures(resp.data)
        resolve(toReturn)
      }).catch(reject)
    })
  }

  fit (mapRef) {
    return new Promise((resolve, reject) => {
      this.fetchFeatures({ convertToFeatures: true }).then((features) => {
        let tmpSource = new VectorSource()
        tmpSource.addFeatures(features)
        // calcul du padding à ajouter, en fonction de la taille de l'écran
        // Les panneaux latéraux font 20% de la taille total
        let padding = 1.5 * screen.availWidth * 0.2
        mapRef.getView().fit(tmpSource.getExtent(), {
          padding: [200, padding, 200, padding],
          duration: 500
        })
        resolve()
      }).catch(reject)
    })
  }

  zoomToLayerExtent (mapRef) {
    this.getLayerExtent().then((extent) => {
      if (extent === undefined) return
      // calcul du padding à ajouter, en fonction de la taille de l'écran
      // Les panneaux latéraux font 20% de la taille total
      let padding = 1.5 * screen.availWidth * 0.2
      mapRef.getView().fit(extent, {
        padding: [200, padding, 200, padding],
        duration: 500
      })
    })
  }
}
